import { AsyncData, Page } from '@model/common';
import { TradeApi } from '@model/iceberg/service/trade/TradeApi';
import { Agent, AuthenticateResponse, GetAgentsResponse } from '@model/agents/agents-models';
import { performNavigate } from '@state/slices/app';
import { AgentPage } from '@model/common/pages/agent-pages';
import { Logging } from '@util/logging';
import Cookies, { Cookie } from '@model/common/cookie/cookie';
import moment from 'moment/moment';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AgentLoginFormValues } from '@model/forms/agents/agent-login-form';

export enum AgentActions {
  NAME = '@AGENTS',
  PERFORM_FETCH_AGENT = '@AGENTS/PERFORM_FETCH_AGENT',
  PERFORM_LOGIN_AGENT = '@AGENTS/PERFORM_LOGIN_AGENT',
  PERFORM_LOGOUT_AGENT = '@AGENTS/PERFORM_LOGOUT_AGENT',
  CLEAR_AGENT = '@AGENTS/clearAgent'
}

export interface LoginError {
  attemptsRemaining: number;
  lockoutTimeRemaining: string;
}

export interface AgentState extends AsyncData<Agent> {
  error: string | LoginError | null;
}

export const INITIAL_AGENT_STATE: AgentState = {
  data: {
    abta: '',
    name: '',
    requiresPayment: false
  },
  loading: false,
  error: null
};

export const loginAgent = createAsyncThunk(
  AgentActions.PERFORM_LOGIN_AGENT,
  async (payload: AgentLoginFormValues, { dispatch, rejectWithValue }) => {
    try {
      const api = new TradeApi();
      const response: AuthenticateResponse = await api.authenticate(payload);

      const cookies = new Cookies();
      cookies.set({
        id: Cookie.TRADE_AUTHORIZATION,
        value: response.loginToken as string,
        options: {
          path: '/',
          domain: cookies.getDomain(),
          expires: moment().add(1, 'days').toDate(),
          sameSite: 'strict',
          secure: true
        }
      });

      await dispatch(fetchAgent());
      dispatch(performNavigate(Page.AGENTS));

      return response;
    } catch (error: any) {
      const tradeRemaining: string = error?.response?.headers['x-ratelimit-trade-remaining'];
      const attemptsRemaining: number = tradeRemaining ? parseInt(tradeRemaining) : 0;
      const lockoutTimeRemaining: string = error?.response?.headers['x-ratelimit-trade-reset'] || '';

      return rejectWithValue({
        attemptsRemaining,
        lockoutTimeRemaining
      });
    }
  }
);

export const logoutAgent = createAsyncThunk(
  AgentActions.PERFORM_LOGOUT_AGENT,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const api: TradeApi = new TradeApi();
      const response = await api.logout();

      // Clear cookies and navigate to login
      const cookies = new Cookies();
      cookies.destroy(Cookie.TRADE_AUTHORIZATION);
      dispatch(performNavigate(Page.AGENTS.concat(AgentPage.LOGIN)));

      return response;
    } catch (error: any) {
      Logging.error({
        text: 'Failed to logout agent'
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAgent = createAsyncThunk(AgentActions.PERFORM_FETCH_AGENT, async (_, { rejectWithValue }) => {
  try {
    const api: TradeApi = new TradeApi();
    const response: GetAgentsResponse = await api.get();
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

const agentSlice = createSlice({
  name: AgentActions.NAME,
  initialState: INITIAL_AGENT_STATE,
  reducers: {
    clearAgent: () => INITIAL_AGENT_STATE
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgent.pending, (state: any = INITIAL_AGENT_STATE) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgent.fulfilled, (state: any = INITIAL_AGENT_STATE, action: any) => {
        return { ...state, data: action.payload, error: null, loading: false };
      })
      .addCase(fetchAgent.rejected, (state: any = INITIAL_AGENT_STATE, action) => {
        state.loading = false;
        state.data = INITIAL_AGENT_STATE.data;
        state.error = action.payload as string;
      })

      .addCase(loginAgent.pending, (state: any = INITIAL_AGENT_STATE) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAgent.fulfilled, (state: any = INITIAL_AGENT_STATE) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(loginAgent.rejected, (state: any = INITIAL_AGENT_STATE, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(logoutAgent.fulfilled, () => INITIAL_AGENT_STATE)
      .addCase(logoutAgent.rejected, (state: any = INITIAL_AGENT_STATE, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { clearAgent } = agentSlice.actions;
export const agentActions = { fetchAgent, loginAgent, logoutAgent, clearAgent };
export const agentReducer = agentSlice.reducer;
